import Papa from 'papaparse';
import type { PurchaseOrder } from '../types';

interface CSVRow {
  'Número de Orden de Compra': string;
  Proveedor: string;
  Artículo: string;
  Ubicación: string;
  'Número de Cuenta Contable': string;
  Clase: string;
  Trade: string;
  Fecha: string;
  'Monto USD': string;
}

class DataService {
  private purchaseOrders: PurchaseOrder[] = [];
  private loaded = false;

  async loadPurchaseOrders(csvPath: string): Promise<PurchaseOrder[]> {
    if (this.loaded) {
      return this.purchaseOrders;
    }

    try {
      const response = await fetch(csvPath);
      const csvText = await response.text();

      return new Promise((resolve, reject) => {
        Papa.parse<CSVRow>(csvText, {
          header: true,
          delimiter: ';', // CSV usa punto y coma como separador
          skipEmptyLines: true,
          complete: (results) => {
            this.purchaseOrders = results.data.map((row, index) => ({
              id: `po-${index + 1}`,
              orderNumber: row['Número de Orden de Compra']?.trim() || '',
              supplier: row.Proveedor?.trim() || '',
              item: row.Artículo?.trim() || '',
              location: row.Ubicación?.trim() || '',
              accountingAccount: row['Número de Cuenta Contable']?.trim() || '',
              class: row.Clase?.trim() || '',
              trade: row.Trade?.trim() || '',
              date: this.parseDate(row.Fecha),
              amountUSD: this.parseAmount(row['Monto USD']),
            }));

            this.loaded = true;
            console.log(`✅ Loaded ${this.purchaseOrders.length} purchase orders`);
            resolve(this.purchaseOrders);
          },
          error: (error: Error) => {
            console.error('❌ Error parsing CSV:', error);
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      throw error;
    }
  }

  private parseDate(dateStr: string): Date {
    if (!dateStr) return new Date();
    
    // Intentar parsear diferentes formatos de fecha
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? new Date() : date;
  }

  private parseAmount(amountStr: string): number {
    if (!amountStr) return 0;
    
    // El CSV usa coma como separador decimal (formato europeo)
    // Convertir coma a punto para parseFloat
    const cleaned = amountStr.trim().replace(',', '.');
    const amount = parseFloat(cleaned);
    return isNaN(amount) ? 0 : amount;
  }

  getPurchaseOrders(): PurchaseOrder[] {
    return this.purchaseOrders;
  }

  getSuppliers(): string[] {
    const suppliers = new Set(
      this.purchaseOrders
        .map(po => po.supplier?.trim())
        .filter(s => s && s.length > 0 && s.length < 200) // Filtrar vacíos y muy largos
    );
    return Array.from(suppliers).sort();
  }

  getCategories(): string[] {
    const categories = new Set(this.purchaseOrders.map(po => po.item));
    return Array.from(categories).sort();
  }

  getLocations(): string[] {
    const locations = new Set(this.purchaseOrders.map(po => po.location));
    return Array.from(locations).sort();
  }

  getAccountingAccounts(): string[] {
    const accounts = new Set(this.purchaseOrders.map(po => po.accountingAccount));
    return Array.from(accounts).sort();
  }

  getClasses(): string[] {
    const classes = new Set(this.purchaseOrders.map(po => po.class));
    return Array.from(classes).sort();
  }

  getTrades(): string[] {
    const trades = new Set(this.purchaseOrders.map(po => po.trade));
    return Array.from(trades).sort();
  }

  getPurchaseOrdersBySupplier(supplier: string): PurchaseOrder[] {
    return this.purchaseOrders.filter(po => po.supplier === supplier);
  }

  getPurchaseOrdersByCategory(category: string): PurchaseOrder[] {
    return this.purchaseOrders.filter(po => po.item === category);
  }

  getPurchaseOrdersByLocation(location: string): PurchaseOrder[] {
    return this.purchaseOrders.filter(po => po.location === location);
  }

  getTotalSpendBySupplier(supplier: string): number {
    return this.getPurchaseOrdersBySupplier(supplier)
      .reduce((total, po) => total + po.amountUSD, 0);
  }

  getTotalSpendByCategory(category: string): number {
    return this.getPurchaseOrdersByCategory(category)
      .reduce((total, po) => total + po.amountUSD, 0);
  }

  getSupplierStats() {
    const suppliers = this.getSuppliers();
    return suppliers.map(supplier => {
      const orders = this.getPurchaseOrdersBySupplier(supplier);
      const categories = new Set(orders.map(po => po.item));
      const locations = new Set(orders.map(po => po.location));
      const dates = orders.map(po => po.date);
      
      return {
        supplier,
        orderCount: orders.length,
        totalSpend: this.getTotalSpendBySupplier(supplier),
        categoriesCount: categories.size,
        locationsCount: locations.size,
        firstOrderDate: new Date(Math.min(...dates.map(d => d.getTime()))),
        lastOrderDate: new Date(Math.max(...dates.map(d => d.getTime()))),
      };
    });
  }

  getCategoryStats() {
    const categories = this.getCategories();
    return categories.map(category => {
      const orders = this.getPurchaseOrdersByCategory(category);
      const suppliers = new Set(orders.map(po => po.supplier));
      
      return {
        category,
        orderCount: orders.length,
        totalSpend: this.getTotalSpendByCategory(category),
        supplierCount: suppliers.size,
      };
    });
  }

  /**
   * Obtiene datos para Supply Master - tabla de proveedores
   * Agrupa por proveedor y calcula métricas
   */
  getSupplierMasterData() {
    const suppliers = this.getSuppliers();
    
    // Filtrar proveedores que parecen ser datos inválidos (notas, descripciones, etc)
    const validSuppliers = suppliers.filter(supplier => {
      // Excluir si comienza con caracteres especiales que indican notas/bullets
      if (/^[•􀀖➢\-\*\d+\.]/.test(supplier)) return false;
      // Excluir si es todo mayúsculas con menos de 3 caracteres (abreviaturas sueltas)
      if (supplier.length < 3) return false;
      // Excluir si parece una descripción técnica (tiene muchos números o símbolos)
      const alphaRatio = (supplier.match(/[a-zA-Z]/g) || []).length / supplier.length;
      if (alphaRatio < 0.4) return false;
      return true;
    });
    
    console.log(`🔍 Proveedores totales: ${suppliers.length}, válidos: ${validSuppliers.length}`);
    
    return validSuppliers.map(supplier => {
      const orders = this.getPurchaseOrdersBySupplier(supplier);
      
      // Calcular artículo principal (el de mayor gasto)
      const categorySpend = new Map<string, number>();
      const accountingAccounts = new Set<string>();
      const classes = new Set<string>();
      const ubicaciones = new Set<string>();
      
      // Agrupar por número de orden para evitar sumar duplicados
      const uniqueOrders = new Map<string, number>();
      
      // Fechas
      let primeraOrdenFecha: Date | undefined = undefined;
      let ultimaOrdenFecha: Date | undefined = undefined;
      
      orders.forEach(po => {
        // Acumular gasto por categoría (sumando líneas)
        const current = categorySpend.get(po.item) || 0;
        categorySpend.set(po.item, current + po.amountUSD);
        
        // Agregar dimensiones
        if (po.accountingAccount) accountingAccounts.add(po.accountingAccount);
        if (po.class) classes.add(po.class);
        if (po.location) ubicaciones.add(po.location);
        
        // Calcular fechas
        if (!primeraOrdenFecha || po.date < primeraOrdenFecha) {
          primeraOrdenFecha = po.date;
        }
        if (!ultimaOrdenFecha || po.date > ultimaOrdenFecha) {
          ultimaOrdenFecha = po.date;
        }
        
        // Agrupar por número de orden - sumar todas las líneas de la misma orden
        if (po.orderNumber) {
          const currentOrderTotal = uniqueOrders.get(po.orderNumber) || 0;
          uniqueOrders.set(po.orderNumber, currentOrderTotal + po.amountUSD);
        }
      });
      
      // Encontrar artículo principal (mayor gasto)
      let mainArticle = '';
      let maxSpend = 0;
      categorySpend.forEach((spend, category) => {
        if (spend > maxSpend) {
          maxSpend = spend;
          mainArticle = category;
        }
      });
      
      // Calcular monto total sumando los montos únicos por orden
      const montoTotalUSD = Array.from(uniqueOrders.values()).reduce((sum, amount) => sum + amount, 0);
      
      // Debug temporal para Movicor
      if (supplier.includes('Movicor')) {
        console.log('🔍 Debug Movicor:', {
          supplier,
          totalLineas: orders.length,
          ordenesUnicas: uniqueOrders.size,
          montoTotal: montoTotalUSD,
          detalleOrdenes: Array.from(uniqueOrders.entries()),
        });
      }
      
      // Generar datos mock para representante (hasta recibir catálogo del cliente)
      const firstNames = ['Juan', 'María', 'Carlos', 'Ana', 'Luis', 'Carmen', 'Pedro', 'Laura'];
      const lastNames = ['García', 'Rodríguez', 'Martínez', 'López', 'González', 'Pérez', 'Sánchez', 'Fernández'];
      const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      
      return {
        id: `supplier-${supplier.toLowerCase().replace(/\s+/g, '-')}`,
        razonSocial: supplier,
        articulo: mainArticle,
        ordenes: uniqueOrders.size, // Cantidad de órdenes únicas
        montoTotalUSD: montoTotalUSD,
        nombreRepresentante: randomFirstName,
        apellidoRepresentante: randomLastName,
        telefonoRepresentante: `+54 11 ${Math.floor(1000 + Math.random() * 8999)}-${Math.floor(1000 + Math.random() * 8999)}`,
        emailRepresentante: `${randomFirstName.toLowerCase()}.${randomLastName.toLowerCase()}@${supplier.toLowerCase().replace(/\s+/g, '')}.com`,
        cuentasContables: Array.from(accountingAccounts).sort(),
        clases: Array.from(classes).sort(),
        ubicaciones: Array.from(ubicaciones).sort(),
        primeraOrdenFecha: primeraOrdenFecha ? (primeraOrdenFecha as Date).toISOString() : new Date().toISOString(),
        ultimaOrdenFecha: ultimaOrdenFecha ? (ultimaOrdenFecha as Date).toISOString() : new Date().toISOString(),
        customFields: {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }).sort((a, b) => b.montoTotalUSD - a.montoTotalUSD); // Ordenar por gasto descendente
  }
}

// Singleton instance
export const dataService = new DataService();
